import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Calendar, Send, User, Clock, LogOut, Pencil, ChevronRight, Star, Gift, QrCode, Trophy, ArrowUpRight, ArrowDownLeft, CalendarClock, Download, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { ResultOverlay } from '../components/ResultOverlay';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const statusColors = { pending: 'bg-yellow-500/15 text-yellow-400', confirmed: 'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400', completed: 'bg-blue-500/15 text-blue-400', refused: 'bg-red-500/15 text-red-400' };
const statusLabels = { pending: 'En attente', confirmed: 'Confirmé', cancelled: 'Annulé', completed: 'Terminé', refused: 'Refusé' };

export default function MonEspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loyalty, setLoyalty] = useState({ points: 0, qr_code: '', transactions: [], rewards: [] });
  const [rdvs, setRdvs] = useState([]);
  const [slots, setSlots] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });
  const [showRdvForm, setShowRdvForm] = useState(false);
  const [rdvForm, setRdvForm] = useState({ date: '', time: '', service: '', message: '' });

  const fetchAll = () => {
    Promise.all([
      api.get('/reservations').catch(() => ({ data: [] })),
      api.get('/loyalty/my-points').catch(() => ({ data: { points: 0, qr_code: '', transactions: [], rewards: [] } })),
      api.get('/rdv/my').catch(() => ({ data: [] })),
      api.get('/rdv/slots').catch(() => ({ data: [] })),
      api.get('/loyalty/my-orders').catch(() => ({ data: [] })),
    ]).then(([r, l, rdv, sl, ord]) => {
      setReservations(r.data);
      setLoyalty(l.data);
      setRdvs(rdv.data);
      setSlots(sl.data);
      setOrders(ord.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleRedeem = async (rewardId) => {
    try {
      await api.post('/loyalty/redeem', { reward_id: rewardId });
      setOverlay({ show: true, type: 'gift', title: 'Bon créé !', subtitle: 'Présentez le QR code au comptoir pour récupérer votre récompense' });
      fetchAll();
    } catch (err) {
      setOverlay({ show: true, type: 'error', title: 'Points insuffisants', subtitle: err.response?.data?.detail || 'Erreur' });
    }
  };

  const handleBookSlot = async (slotId) => {
    try {
      await api.post('/rdv/book', { slot_id: slotId });
      setOverlay({ show: true, type: 'party', title: 'Demande envoyée !', subtitle: 'Nous vous confirmerons votre RDV sous 24-48h' });
      fetchAll();
    } catch (e) {
      setOverlay({ show: true, type: 'error', title: 'Erreur', subtitle: e.response?.data?.detail || 'Créneau indisponible' });
    }
  };

  const handleCancelRdv = async (rdvId) => {
    try {
      await api.delete(`/rdv/${rdvId}`);
      setOverlay({ show: true, type: 'success', title: 'RDV annulé', subtitle: 'Votre rendez-vous a été annulé' });
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleRequestRdv = async (e) => {
    e.preventDefault();
    if (!rdvForm.date || !rdvForm.time) {
      toast.error('Veuillez remplir la date et l\'heure');
      return;
    }
    try {
      await api.post('/reservations', {
        type: 'rdv',
        prestation_type: rdvForm.service,
        date: rdvForm.date,
        message: `Demande de RDV le ${rdvForm.date} à ${rdvForm.time}${rdvForm.service ? ` - ${rdvForm.service}` : ''}. ${rdvForm.message}`,
        name: user?.name,
        email: user?.email,
        phone: user?.phone,
      });
      setOverlay({ show: true, type: 'party', title: 'Demande envoyée !', subtitle: 'Nous vous confirmerons votre RDV sous 24-48h par email' });
      setShowRdvForm(false);
      setRdvForm({ date: '', time: '', service: '', message: '' });
      fetchAll();
    } catch {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleLogout = () => {
    setOverlay({ show: true, type: 'success', title: 'À bientôt !', subtitle: 'Vous avez été déconnecté' });
    setTimeout(() => { logout(); navigate('/'); }, 1800);
  };
  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 fade-in" data-testid="mon-espace-page">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-light tracking-tight" style={{ fontFamily: 'Outfit' }}>
            {"Bonjour, "}<span className="text-[#D4AF37]">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-sm text-[#A1A1AA]">{user?.email}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/profile" data-testid="edit-profile-btn"><Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white"><Pencil className="w-4 h-4 mr-1" /> Profil</Button></Link>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300" data-testid="espace-logout-btn"><LogOut className="w-4 h-4 mr-1" /> Quitter</Button>
        </div>
      </div>

      {/* Points summary card */}
      <div className="bg-gradient-to-r from-[#D4AF37]/15 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-2xl p-6 mb-6 flex items-center gap-6" data-testid="points-card">
        <div className="flex-1">
          <p className="text-xs text-[#D4AF37] font-medium mb-1">{"Mes points fidélité"}</p>
          <div className="text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{loyalty.points}</div>
          <p className="text-[10px] text-[#A1A1AA] mt-1">{"Échangez vos points contre des récompenses"}</p>
        </div>
        <div className="bg-[#D4AF37]/15 p-3 rounded-xl"><Star className="w-8 h-8 text-[#D4AF37]" /></div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/5 h-auto p-1 mb-6 w-full justify-start overflow-x-auto">
          {[
            { v: 'overview', l: 'Aperçu', icon: User },
            { v: 'points', l: 'Mes points', icon: Star },
            { v: 'rewards', l: 'Récompenses', icon: Gift },
            { v: 'mybons', l: 'Mes bons', icon: Trophy },
            { v: 'qrcode', l: 'Mon QR', icon: QrCode },
            { v: 'rdv', l: 'Mes RDV', icon: CalendarClock },
            { v: 'reservations', l: 'Demandes', icon: Calendar },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-xs px-3 gap-1.5">
              <t.icon className="w-3.5 h-3.5" /> {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <Link to="/reservation" className="card-hover bg-[#D4AF37]/8 border border-[#D4AF37]/20 rounded-2xl p-5 flex items-center gap-4 block" data-testid="espace-new-reservation">
              <div className="bg-[#D4AF37]/15 p-3 rounded-xl"><Send className="w-5 h-5 text-[#D4AF37]" /></div>
              <div><h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Nouvelle demande</h3><p className="text-xs text-[#A1A1AA]">Demander un devis</p></div>
              <ChevronRight className="w-4 h-4 text-[#A1A1AA] ml-auto" />
            </Link>
            <Link to="/contact" className="card-hover bg-[#121212] border border-white/5 rounded-2xl p-5 flex items-center gap-4 block" data-testid="espace-contact">
              <div className="bg-white/5 p-3 rounded-xl"><User className="w-5 h-5 text-[#A1A1AA]" /></div>
              <div><h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Nous contacter</h3><p className="text-xs text-[#A1A1AA]">06 10 50 72 93 (SMS)</p></div>
              <ChevronRight className="w-4 h-4 text-[#A1A1AA] ml-auto" />
            </Link>
          </div>
          {reservations.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit' }}>{"Dernières demandes"}</h3>
              {reservations.slice(0, 3).map(r => (
                <div key={r.reservation_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 mb-2 flex items-center justify-between">
                  <div><span className="text-sm font-medium">{r.prestation_type || r.type || 'Demande'}</span><p className="text-[10px] text-[#A1A1AA]">{new Date(r.created_at).toLocaleDateString('fr-FR')}</p></div>
                  <Badge className={statusColors[r.status] || 'bg-white/10'}>{statusLabels[r.status] || r.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Points */}
        <TabsContent value="points">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{"Historique de mes points"}</h3>
          {loyalty.transactions.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] text-center py-8">Aucune transaction</p>
          ) : (
            <div className="space-y-2">
              {loyalty.transactions.map(tx => (
                <div key={tx.tx_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-3">
                  <div className={`p-1.5 rounded-md ${tx.amount > 0 ? 'bg-[#10B981]/10' : 'bg-[#EF4444]/10'}`}>
                    {tx.amount > 0 ? <ArrowDownLeft className="w-4 h-4 text-[#10B981]" /> : <ArrowUpRight className="w-4 h-4 text-[#EF4444]" />}
                  </div>
                  <div className="flex-1"><div className="text-sm">{tx.description}</div><div className="text-[10px] text-[#A1A1AA]">{new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div></div>
                  <div className={`text-sm font-bold ${tx.amount > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} style={{ fontFamily: 'JetBrains Mono' }}>{tx.amount > 0 ? '+' : ''}{tx.amount} pts</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Rewards */}
        <TabsContent value="rewards">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{"Récompenses disponibles"}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {loyalty.rewards.map(r => {
              const canRedeem = loyalty.points >= r.cost_points;
              return (
                <div key={r.reward_id} className={`bg-[#121212] border rounded-2xl p-5 ${canRedeem ? 'border-[#D4AF37]/30' : 'border-white/5 opacity-60'}`} data-testid={`reward-${r.reward_id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-[#D4AF37]" />
                    <h4 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>{r.name}</h4>
                  </div>
                  <p className="text-xs text-[#A1A1AA] mb-3">{r.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{r.cost_points} pts</span>
                    <Button onClick={() => handleRedeem(r.reward_id)} disabled={!canRedeem} size="sm"
                      className={canRedeem ? 'bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full text-xs' : 'bg-white/5 text-[#A1A1AA] rounded-full text-xs'} data-testid={`redeem-${r.reward_id}`}>
                      {canRedeem ? 'Commander' : `${r.cost_points - loyalty.points} pts manquants`}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Mes bons (reward QR codes) */}
        <TabsContent value="mybons">
          <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>{"Mes bons de récompense"}</h3>
          {orders.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] text-center py-8">{"Aucun bon. Échangez vos points contre des récompenses !"}</p>
          ) : (
            <div className="space-y-4">
              {orders.map(o => (
                <div key={o.order_id} className={`bg-[#121212] border rounded-2xl p-5 ${o.status === 'pending' ? 'border-[#D4AF37]/30' : o.status === 'confirmed' ? 'border-[#10B981]/30' : 'border-white/5'}`} data-testid={`bon-${o.order_id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-sm">{o.reward_name}</h4>
                      <p className="text-[10px] text-[#A1A1AA]">{o.cost_points} pts - {new Date(o.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <Badge className={o.status === 'pending' ? 'bg-yellow-500/15 text-yellow-400' : o.status === 'confirmed' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}>
                      {o.status === 'pending' ? 'À utiliser' : o.status === 'confirmed' ? 'Utilisé' : 'Refusé'}
                    </Badge>
                  </div>
                  {o.status === 'pending' && o.qr_token && (
                    <div className="text-center bg-white p-4 rounded-xl">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(o.qr_token)}&bgcolor=FFFFFF&color=000000`}
                        alt="QR Bon" className="w-36 h-36 mx-auto" />
                      <p className="text-[10px] text-gray-500 mt-2">{"Présentez ce QR code au comptoir"}</p>
                    </div>
                  )}
                  {o.status === 'confirmed' && (
                    <p className="text-xs text-[#10B981] text-center">{"Bon validé"} {o.validated_at ? `le ${new Date(o.validated_at).toLocaleDateString('fr-FR')}` : ''}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* QR Code */}
        <TabsContent value="qrcode">
          <div className="text-center py-6">
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>{"Mon QR Code Fidélité"}</h3>
            <p className="text-sm text-[#A1A1AA] mb-6">{"Présentez ce QR code lors de vos événements pour cumuler des points"}</p>
            <div className="inline-block bg-white p-6 rounded-2xl mb-4" data-testid="qr-code-display">
              {loyalty.qr_code && (
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(loyalty.qr_code)}&bgcolor=FFFFFF&color=000000`}
                  alt="QR Code" className="w-48 h-48" />
              )}
            </div>
            <p className="text-xs text-[#A1A1AA]">ID: {loyalty.qr_code?.slice(0, 8)}...</p>
          </div>
        </TabsContent>

        {/* RDV */}
        <TabsContent value="rdv">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Mes Rendez-vous</h3>
            <Button onClick={() => setShowRdvForm(!showRdvForm)} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5 text-xs" data-testid="request-rdv-btn">
              <Plus className="w-4 h-4 mr-1" /> Demander un RDV
            </Button>
          </div>

          {/* Formulaire demande RDV */}
          {showRdvForm && (
            <form onSubmit={handleRequestRdv} className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-2xl p-5 mb-6 space-y-4" data-testid="rdv-request-form">
              <h4 className="text-sm font-semibold text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>{"Demander un rendez-vous"}</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A1A1AA] mb-1 block">{"Date souhaitée *"}</Label>
                  <Input type="date" value={rdvForm.date} onChange={e => setRdvForm(p => ({ ...p, date: e.target.value }))} required
                    data-testid="rdv-form-date" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs text-[#A1A1AA] mb-1 block">{"Heure souhaitée *"}</Label>
                  <Input type="time" value={rdvForm.time} onChange={e => setRdvForm(p => ({ ...p, time: e.target.value }))} required
                    data-testid="rdv-form-time" className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1 block">Service</Label>
                <Select value={rdvForm.service} onValueChange={v => setRdvForm(p => ({ ...p, service: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 rounded-xl" data-testid="rdv-form-service"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="DJ & Animation">DJ & Animation</SelectItem>
                    <SelectItem value="Bar à Mocktails">{"Bar à Mocktails"}</SelectItem>
                    <SelectItem value="Serveur à Domicile">{"Serveur à Domicile"}</SelectItem>
                    <SelectItem value="Prestation complète">{"Prestation complète"}</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA] mb-1 block">Message (optionnel)</Label>
                <Textarea value={rdvForm.message} onChange={e => setRdvForm(p => ({ ...p, message: e.target.value }))} rows={2}
                  placeholder={"Décrivez votre besoin..."} data-testid="rdv-form-message"
                  className="bg-[#0A0A0A] border-white/10 focus:border-[#D4AF37] rounded-xl resize-none" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6 text-sm" data-testid="rdv-form-submit">
                  <Send className="w-4 h-4 mr-2" /> Envoyer ma demande
                </Button>
                <Button type="button" onClick={() => setShowRdvForm(false)} variant="outline" className="border-white/10 rounded-full px-4 text-xs">Annuler</Button>
              </div>
            </form>
          )}

          {/* Available slots */}
          {slots.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-[#D4AF37] mb-3">{"Créneaux disponibles"}</h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {slots.map(s => (
                  <div key={s.slot_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center justify-between" data-testid={`slot-${s.slot_id}`}>
                    <div>
                      <div className="text-sm font-medium">{new Date(s.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} à {s.time}</div>
                      <div className="text-xs text-[#A1A1AA]">{s.duration} min {s.service && `- ${s.service}`}</div>
                    </div>
                    <Button size="sm" onClick={() => handleBookSlot(s.slot_id)}
                      className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full text-xs" data-testid={`book-slot-${s.slot_id}`}>Réserver</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* My RDV */}
          {rdvs.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] text-center py-8">Aucun rendez-vous</p>
          ) : (
            <div className="space-y-3">
              {rdvs.map(r => (
                <div key={r.rdv_id} className="bg-[#121212] border border-white/5 rounded-xl p-4" data-testid={`my-rdv-${r.rdv_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-medium">{new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {r.time}</div>
                      <div className="text-xs text-[#A1A1AA]">{r.duration} min {r.service && `- ${r.service}`}</div>
                    </div>
                    <Badge className={statusColors[r.status] || 'bg-white/10'}>{statusLabels[r.status] || r.status}</Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {r.status === 'confirmed' && (
                      <>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rdv/${r.rdv_id}/ics`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost" className="text-[#D4AF37] text-xs h-7"><Download className="w-3 h-3 mr-1" /> .ics</Button>
                        </a>
                        {r.google_cal_link && (
                          <a href={r.google_cal_link} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" className="text-[#D4AF37] text-xs h-7"><Calendar className="w-3 h-3 mr-1" /> Google Cal</Button>
                          </a>
                        )}
                      </>
                    )}
                    {(r.status === 'pending' || r.status === 'confirmed') && (
                      <Button size="sm" variant="ghost" onClick={() => handleCancelRdv(r.rdv_id)}
                        className="text-red-400 text-xs h-7"><X className="w-3 h-3 mr-1" /> Annuler</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reservations */}
        <TabsContent value="reservations">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Mes demandes</h3>
            <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] text-xs">{reservations.length}</Badge>
          </div>
          {reservations.length === 0 ? (
            <div className="text-center py-12 text-[#A1A1AA]">
              <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm mb-4">Aucune demande</p>
              <Link to="/reservation"><Button className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6 text-sm">Faire une demande</Button></Link>
            </div>
          ) : (
            <div className="space-y-3">
              {reservations.map(r => (
                <div key={r.reservation_id} className="bg-[#121212] border border-white/5 rounded-2xl p-5" data-testid={`espace-reservation-${r.reservation_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-semibold capitalize">{r.prestation_type === 'dj' ? 'DJ & Animation' : r.prestation_type === 'bar' ? 'Bar à Mocktails' : r.prestation_type === 'serveur' ? 'Serveur à Domicile' : r.prestation_type === 'complet' ? 'Prestation complète' : r.type || 'Demande'}</span>
                    <Badge className={statusColors[r.status] || 'bg-white/10'}>{statusLabels[r.status] || r.status}</Badge>
                  </div>
                  {r.message && <p className="text-sm text-[#A1A1AA] mb-2 line-clamp-2">{r.message}</p>}
                  <div className="flex gap-4 text-xs text-[#A1A1AA]">
                    {r.guests && <span>{r.guests} invités</span>}
                    {r.location && <span>{r.location}</span>}
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
